/* eslint-disable */

import { PencilLine, Save, Trash, X } from "lucide-react";
import { Button } from "../utils/components/ui/button";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import React from "react";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../utils/components/ui/accordion";
import TableComponent from "../components/common/TableComponent";
import { useAddCustomer, useGetAllCustomers } from "../service/customer";
import { toast } from "react-toastify";
import useCustomerStore from "../store/useCustomerStore";
import { Combobox } from "../components/Combobox";
import { ProductDropdown } from "../components/ProductDropdown";
import { useEffect, useState } from "react";
import { useGetAllProductData } from "../service/productService";
import { useQueryManager } from "../hooks/useQueryManager";
import {
  useAddQuotation,
  useGetQuotationById,
  useUpdateQuotation,
} from "../service/quatationService";
import { useNavigate } from "react-router-dom";
import QuoteSuccessModal from "../components/SuccessModal";
import { useQueryClient } from "@tanstack/react-query";
import { QuotationProduct } from "../types/quatationType";
import { cn } from "../utils/cn";
import { useAddInvoice } from "../service/invoiseService";
import {
  calculateBaseQuantity,
  calculateUnitPrice,
  getDefaultUnitOption,
  getUnitOption,
  normalizeUnitOptions,
} from "../utils/unitConversion";
import {
  findProductById,
  formatProductDropdownLabel,
} from "../utils/productDropdown";

const customerSchema = z.object({
  name: z.string().optional(),
  phoneNumber: z.string().min(1, "Phone Number is required"),
  email: z.string().nullable().optional(),
  address: z.string().nullable().optional(),
});

type CustomerFormInputs = z.infer<typeof customerSchema>;

// 1. Zod schema for product entry
const productSchema = z.object({
  productId: z.number().min(1, "Product is required"),
  quantity: z.coerce.number().positive("Quantity is required"),
  unitPrice: z.coerce.number().min(0, "Unit price is required"),
  discountAmount: z.coerce.number().min(0, "Discount is required"),
  isDiscountPercentage: z
    .union([z.boolean(), z.string()])
    .transform((val) => val === true || val === "true"),
  taxPercentage: z.coerce.number().min(0, "Tax is required"),
  unit: z.string().default(""),
  conversionToBaseUnit: z.coerce.number().positive().default(1),
  baseQuantity: z.coerce.number().min(0).default(0),
  remark: z.string().optional().default(""),
});
type ProductFormInputs = z.output<typeof productSchema>;

// Edit Product Modal Component
interface EditProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: ProductFormInputs) => void;
  productData: ProductFormInputs | null;
  products: any[];
  editIndex: number | null;
}

const EditProductModal: React.FC<EditProductModalProps> = ({
  isOpen,
  onClose,
  onSave,
  productData,
  products,
  editIndex,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<ProductFormInputs>({
    resolver: zodResolver(productSchema),
    defaultValues: productData || {
      productId: 0,
      quantity: 1,
      unitPrice: 0,
      discountAmount: 0,
      isDiscountPercentage: true,
      taxPercentage: 5,
      unit: "",
      conversionToBaseUnit: 1,
      baseQuantity: 0,
      remark: "",
    },
  });

  const watchedProductId = watch("productId");

  React.useEffect(() => {
    if (productData) {
      reset(productData);
    }
  }, [productData, reset]);

  React.useEffect(() => {
    const foundProduct = products?.find((p: any) => p.id === watchedProductId);
    if (foundProduct) {
      setValue(
        "unitPrice",
        calculateUnitPrice(
          foundProduct.price,
          productData?.conversionToBaseUnit || 1,
        ),
      );
      // Only update unit and conversion if they are not already set correctly for this product
      // This prevents overriding the user's selected unit when opening the modal for editing
      if (!productData || productData.productId !== watchedProductId) {
        const option = getDefaultUnitOption(
          foundProduct.unitOptions,
          foundProduct.unit,
        );
        setValue("unit", option?.unit || foundProduct.unit || "");
        setValue("conversionToBaseUnit", option?.conversionToBaseUnit || 1);
      }
    }
  }, [watchedProductId, productData, products, setValue]);

  const onSubmit = (data: ProductFormInputs) => {
    onSave(data);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-[#1A5EF9]">
            Edit Product {editIndex !== null ? `#${editIndex + 1}` : ""}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Product Selection */}
            <div className="flex flex-col">
              <label
                htmlFor="productId"
                className="mb-2 text-[#84928C] font-medium"
              >
                Product *
              </label>
              <ProductDropdown
                useApiFilter={true}
                className="w-full h-11 rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4"
                products={products}
                onChange={(val) => {
                  setValue("productId", Number(val), { shouldValidate: true });
                }}
                value={String(watchedProductId || "")}
              />
              {errors.productId && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.productId.message}
                </p>
              )}
            </div>

            {/* Quantity */}
            <div className="flex flex-col">
              <label
                htmlFor="quantity"
                className="mb-2 text-[#84928C] font-medium"
              >
                Quantity *
              </label>
              <input
                id="quantity"
                type="number"
                min="0.0001"
                step="any"
                placeholder="Quantity"
                className="h-[50px] border-2 border-[#F3F4F3] bg-[#F3F4F3] px-4 rounded-md focus:border-[#1A5EF9] focus:outline-none"
                {...register("quantity")}
              />
              {errors.quantity && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.quantity.message}
                </p>
              )}
            </div>

            {/* Unit */}
            <div className="flex flex-col">
              <label htmlFor="unit" className="mb-2 text-[#84928C] font-medium">
                Unit
              </label>
              <select
                id="unit"
                className="h-[50px] border-2 border-[#F3F4F3] bg-[#F3F4F3] px-4 rounded-md focus:border-[#1A5EF9] focus:outline-none"
                {...register("unit")}
                onChange={(event) => {
                  const product = findProductById(products, watchedProductId);
                  const option = getUnitOption(
                    product?.unitOptions,
                    event.target.value,
                    product?.unit,
                  );
                  setValue("unit", event.target.value, {
                    shouldValidate: true,
                  });
                  setValue(
                    "conversionToBaseUnit",
                    option?.conversionToBaseUnit || 1,
                  );
                  setValue(
                    "unitPrice",
                    calculateUnitPrice(
                      product?.price || 0,
                      option?.conversionToBaseUnit || 1,
                    ),
                  );
                }}
              >
                {normalizeUnitOptions(
                  findProductById(products, watchedProductId)?.unitOptions,
                  findProductById(products, watchedProductId)?.unit,
                ).map((option) => (
                  <option key={option.unit} value={option.unit}>
                    {option.unit}
                  </option>
                ))}
              </select>
            </div>

            {/* Unit Price */}
            <div className="flex flex-col">
              <label
                htmlFor="unitPrice"
                className="mb-2 text-[#84928C] font-medium"
              >
                Unit Price
              </label>
              <input
                id="unitPrice"
                type="number"
                min="0"
                step="any"
                placeholder="Unit Price"
                className="h-[50px] border-2 border-[#F3F4F3] bg-[#F3F4F3] px-4 rounded-md focus:border-[#1A5EF9] focus:outline-none"
                {...register("unitPrice")}
              />
              {errors.unitPrice && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.unitPrice.message}
                </p>
              )}
            </div>

            {/* Discount Amount */}
            <div className="flex flex-col">
              <label
                htmlFor="discountAmount"
                className="mb-2 text-[#84928C] font-medium"
              >
                Discount
              </label>
              <input
                id="discountAmount"
                type="number"
                min="0"
                step="any"
                placeholder="Discount"
                className="h-[50px] border-2 border-[#F3F4F3] bg-[#F3F4F3] px-4 rounded-md focus:border-[#1A5EF9] focus:outline-none"
                {...register("discountAmount")}
              />
              {errors.discountAmount && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.discountAmount.message}
                </p>
              )}
            </div>

            {/* Discount Type */}
            <div className="flex flex-col">
              <label
                htmlFor="isDiscountPercentage"
                className="mb-2 text-[#84928C] font-medium"
              >
                Discount Type
              </label>
              <select
                id="isDiscountPercentage"
                className="h-[50px] border-2 border-[#F3F4F3] bg-[#F3F4F3] px-4 rounded-md focus:border-[#1A5EF9] focus:outline-none"
                {...register("isDiscountPercentage")}
              >
                <option value="true">Percentage (%)</option>
                <option value="false">Fixed Amount</option>
              </select>
            </div>

            {/* Tax Percentage */}
            <div className="flex flex-col">
              <label
                htmlFor="taxPercentage"
                className="mb-2 text-[#84928C] font-medium"
              >
                Tax (%)
              </label>
              <input
                id="taxPercentage"
                type="number"
                min="0"
                step="any"
                placeholder="Tax Percentage"
                className="h-[50px] border-2 border-[#F3F4F3] bg-gray-100 px-4 rounded-md cursor-not-allowed"
                disabled
                {...register("taxPercentage")}
              />
              {errors.taxPercentage && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.taxPercentage.message}
                </p>
              )}
            </div>

            {/* Remark */}
            <div className="flex flex-col md:col-span-2 lg:col-span-3">
              <label
                htmlFor="remark"
                className="mb-2 text-[#84928C] font-medium"
              >
                Remark
              </label>
              <input
                id="remark"
                type="text"
                placeholder="Enter remark for this item"
                className="h-[50px] border-2 border-[#F3F4F3] bg-[#F3F4F3] px-4 rounded-md focus:border-[#1A5EF9] focus:outline-none"
                {...register("remark")}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-4 mt-8 pt-4 border-t">
            <Button
              type="button"
              onClick={onClose}
              className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded-md transition-colors"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-[#1A5EF9] hover:bg-[#7AB01F] text-white px-6 py-2 rounded-md transition-colors"
            >
              Update Product
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

const SaveInvoice = () => {
  const { allParams, setValue, getValues } = useQueryManager();

  const { data: draftData } = useGetQuotationById(getValues("id")[0]);

  const { setCurrentCustomer, currentCustomer, clearCurrentCustomer } =
    useCustomerStore();

  const [productFilter, setProductFilter] = useState("");

  // Modal state for editing
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingProduct, setEditingProduct] =
    useState<ProductFormInputs | null>(null);

  const handleDeleteProduct = (index: number) => {
    setProductArray((prev) => prev.filter((_, i) => i !== index));
    toast.success("Product deleted successfully!");
  };

  // Handle edit product
  const handleEditProduct = (index: number) => {
    setEditingIndex(index);
    setEditingProduct(productArray[index]);
    setIsEditModalOpen(true);
  };

  // Handle save edited product
  const handleSaveEditedProduct = (updatedProduct: ProductFormInputs) => {
    if (editingIndex !== null) {
      setProductArray((prev) =>
        prev.map((item, index) =>
          index === editingIndex
            ? {
                ...updatedProduct,
                baseQuantity: calculateBaseQuantity(
                  updatedProduct.quantity,
                  updatedProduct.conversionToBaseUnit,
                ),
              }
            : item,
        ),
      );
      toast.success("Product updated successfully!");
    }
    setIsEditModalOpen(false);
    setEditingIndex(null);
    setEditingProduct(null);
  };

  // Close modal handler
  const handleCloseModal = () => {
    setIsEditModalOpen(false);
    setEditingIndex(null);
    setEditingProduct(null);
  };

  const brandsTColumns = [
    { header: "Sl.", accessor: "sl" },
    { header: "Product", accessor: "product" },
    { header: "In Arabic", accessor: "inArabic" },
    { header: "Quantity", accessor: "quantity" },
    { header: "Unit", accessor: "unit" },
    { header: "Tax", accessor: "tax" },
    { header: "Unit Price", accessor: "unitPrice" },
    { header: "Discount", accessor: "discount" },
    { header: "Total", accessor: "total" },
    {
      header: "Action",
      accessor: "action",
      className: "text-center",
      cell: ({ row }: any) => (
        <div className="flex gap-x-4 justify-center">
          <button
            className="text-gray-500 hover:text-[#1A5EF9] cursor-pointer transition-colors"
            onClick={() => handleEditProduct(row.original.sl - 1)}
            title="Edit Product"
            aria-label="Edit Product"
          >
            <PencilLine className="w-5 h-5" />
            <span className="sr-only">Edit</span>
          </button>

          <button
            className="text-gray-500 hover:text-red-500 cursor-pointer transition-colors"
            onClick={() => handleDeleteProduct(row.original.sl - 1)}
            title="Delete Product"
            aria-label="Delete Product"
          >
            <Trash className="w-5 h-5" />
            <span className="sr-only">Delete</span>
          </button>
        </div>
      ),
    },
  ];

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<CustomerFormInputs>({
    resolver: zodResolver(customerSchema),
    defaultValues: currentCustomer
      ? {
          name: currentCustomer.name || "",
          phoneNumber: currentCustomer.phoneNumber || "",
          email: currentCustomer.email || "",
          address: currentCustomer.address || "",
        }
      : {},
  });

  const [selected, setSelected] = useState<string>("");
  const [selectedProduct, setSelectedProduct] = useState<string>("");
  const [userOptions, setUserOptions] = useState();
  const branchId = Number(localStorage.getItem("branchId")) || undefined;
  const { data: customer, refetch } = useGetAllCustomers({
    customerName: selected,
  });

  const { data: productData } = useGetAllProductData({
    pageNo: 0,
    pageSize: 1000,
    status: true,
    keyword: productFilter,
    branchId,
  });
  const products = productData?.productResponseList || [];
  console.log(products, "products");
  console.log("customer", customer);
  console.log(selectedProduct, "selected");
  console.log(currentCustomer, "curr");
  const navigate = useNavigate();

  const addQuotation = useAddQuotation({
    onSuccess: (data) => {
      toast.success("Quotation added successfully!");
      // Optionally reset productArray or navigate
      setProductArray([]);
      // navigate("/sales")
      setIsSuccessModalOpen(true);
      clearCurrentCustomer();

      // You can also add navigation or other logic here
    },
    onError: (error) => {
      toast.error(`Failed to add quotation due to ${error.message}.`);
    },
  });

  useEffect(() => {
    refetch();
    setUserOptions(
      customer?.customerResponse?.map((data) => ({
        value: data?.id,
        label: data?.username,
      })) || [],
    );

    if (currentCustomer && customer?.customerResponse) {
      const foundCustomer = customer.customerResponse.find(
        (c: any) => c.id === currentCustomer || c.id === currentCustomer?.id,
      );
      if (foundCustomer && currentCustomer !== foundCustomer) {
        setCurrentCustomer(foundCustomer);
      }
    }

    // Reset form fields when currentCustomer changes
    if (currentCustomer) {
      reset({
        name: currentCustomer.name || "",
        phoneNumber: currentCustomer.phoneNumber || "",
        email: currentCustomer.email || "",
        address: currentCustomer.address || "",
      });
    }
  }, [customer, currentCustomer, reset, refetch]);

  useEffect(() => {
    setUserOptions(
      customer?.customerResponse?.map((data) => ({
        value: data?.id,
        label: data?.username,
      })) || [],
    );

    if (currentCustomer && customer?.customerResponse) {
      const foundCustomer = customer.customerResponse.find(
        (c: any) => c.id === currentCustomer || c.id === currentCustomer?.id,
      );
      if (foundCustomer && currentCustomer !== foundCustomer) {
        setCurrentCustomer(foundCustomer);
      }
    }

    // Reset form fields when currentCustomer changes
    if (currentCustomer) {
      reset({
        name: currentCustomer.name || "",
        phoneNumber: currentCustomer.phoneNumber || "",
        email: currentCustomer.email || "",
        address: currentCustomer.address || "",
      });
    }
  }, [customer, currentCustomer, reset]);

  // 2. State for product array
  const [productArray, setProductArray] = useState<ProductFormInputs[]>([]);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [quoteId, setQuoteId] = useState<string | null>(null);
  const [roundoff, setRoundoff] = useState("");
  const [paymentMode, setPaymentMode] = useState("");
  const [reqNumber, setReqNumber] = useState("");
  const [chqNumber, setChqNumber] = useState(""); // 1. Add state
  const [cardNumber, setCardNumber] = useState(""); // 1. Add state
  const [onlinePaymentRefNo, setOnlinePaymentRefNo] = useState(""); // Online payment reference number
  const [lpoNumber, setLpoNumber] = useState("");
  const [invoiceRemark, setInvoiceRemark] = useState(""); // Global invoice remark

  useEffect(() => {
    setPayment((prev) => ({
      ...prev,
      amountPaid: draftData?.paidAmount ? draftData.paidAmount.toString() : "",
    }));
  }, [draftData]);

  // Payment state and handler
  const [payment, setPayment] = useState<{
    paymentMethod: string;
    amountPaid: string;
  }>({
    paymentMethod: "",
    amountPaid: draftData?.paidAmount ? draftData.paidAmount.toString() : "",
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPayment((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCloseSuccessModal = () => {
    setIsSuccessModalOpen(false);
    navigate("/sales");
  };

  const handlePrintQuote = () => {
    // Implement print logic here
    // For now, just close the modal
    handleCloseSuccessModal();
  };

  // Add handleContinue function
  const handleContinue = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    // Add your logic for continuing payment here
    toast.info("Continue button clicked!");
  };

  const SaveInvoice = useAddInvoice({
    onSuccess: (data) => {
      console.log("Mutation response:", data);
      toast.success("Success");
      navigate("/sales/invoice");
    },
    onError: (error: Error) => {
      console.error("Error saving invoice:", error.message);
      toast.error(`Failed to save invoice: ${error.message}`);
    },
  });

  const onSaveQuotation = () => {
    if (!currentCustomer || !currentCustomer.id) {
      toast.error("Please select a customer before saving the quotation.");
      return;
    }
    if (allParams.id) {
      const postData: any = {
        id: Number(allParams.id),
        customerId: draftData?.customer?.id || currentCustomer?.id || 0,
        roundingAmount: roundoff,
        paymentMode: payment?.paymentMethod,
        paidAmount: Number(payment?.amountPaid),
        reqNumber,
        lpo: lpoNumber,
        remark: invoiceRemark,
        salesProductRequests: draftData?.quotationProducts?.map(
          (item: QuotationProduct) => ({
            id: item.id,
            productId: item.product.id,
            purchaseEntryProductId: item.purchaseEntryProductId || 0,
            quantity: item.qty,
            unitPrice: item.unitPrice,
            discountAmount: item.discountAmount,
            taxPercentage: item.rateOfTax,
            unit: item.unit || item.product.unit || "",
            conversionToBaseUnit: item.conversionToBaseUnit || 1,
            baseQuantity: item.baseQuantity || item.qty,
            remark: item.remark || "",
          }),
        ),
      };
      if (payment.paymentMethod === "CHEQUE") {
        postData.chqNumber = chqNumber;
      }
      if (payment.paymentMethod === "CARD") {
        postData.cardNumber = cardNumber;
      }
      if (payment.paymentMethod === "ONLINE_PAYMENT") {
        postData.onlinePaymentRefNo = onlinePaymentRefNo;
      }
      SaveInvoice.mutate({
        ...postData,
        id: allParams.id,
      });
    } else {
      const postData: any = {
        customerId: currentCustomer.id as string,
        quotationProductRequests: productArray,
        isDraft: false,
        reqNumber,
        lpo: lpoNumber,
      };
      if (payment.paymentMethod === "CHEQUE") {
        postData.chqNumber = chqNumber;
      }
      if (payment.paymentMethod === "CARD") {
        postData.cardNumber = cardNumber;
      }
      if (payment.paymentMethod === "ONLINE_PAYMENT") {
        postData.onlinePaymentRefNo = onlinePaymentRefNo;
      }
      addQuotation.mutate(postData, {
        onSuccess: (data: any) => {
          setQuoteId(data?.id || null);
        },
      });
    }
  };

  useEffect(() => {
    if (draftData && draftData.customer) {
      reset({
        name: draftData.customer.username || "",
        phoneNumber: draftData.customer.phoneNumber || "",
        email: draftData.customer.email || "",
        address: draftData.customer.address || "",
      });
      setCurrentCustomer(draftData.customer);
    }
    setReqNumber(draftData?.reqNumber || "");
    setLpoNumber(draftData?.lpo || "");
    // If you want to set product array as well:
    if (draftData && draftData.quotationProducts) {
      setProductArray(
        draftData.quotationProducts?.map((data: QuotationProduct) => ({
          productId: data.product?.id ?? 0,
          quantity: data.qty ?? 1,
          unitPrice: data.unitPrice ?? 0,
          discountAmount: data.discountAmount ?? 0,
          isDiscountPercentage: data.isDiscountPercentage ?? true,
          taxPercentage: data.rateOfTax ?? 0,
          unit: data.unit || data.product?.unit || "",
          conversionToBaseUnit: data.conversionToBaseUnit || 1,
          baseQuantity: data.baseQuantity || data.qty || 0,
          remark: data.remark || "",
        })) || [],
      );
    }
  }, [draftData, reset, setCurrentCustomer]);

  const PAYMENT_MODES = [
    { value: "CHEQUE", label: "Cheque" },
    { value: "CASH", label: "Cash" },
    { value: "CARD", label: "Card" },
    { value: "ONLINE_PAYMENT", label: "Online" },
  ];

  const inputCls =
    "h-11 w-full rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]";
  const labelCls = "text-sm font-medium text-[#52627C] mb-1.5 block";

  const grandTotal =
    productArray.reduce((sum, item) => {
      const base =
        item.unitPrice * item.quantity -
        (item.isDiscountPercentage
          ? (item.unitPrice * item.quantity * item.discountAmount) / 100
          : item.discountAmount);
      return sum + base * (1 + item.taxPercentage / 100);
    }, 0) - (Number(roundoff) || 0);

  return (
    <>
      <div className="space-y-6 p-6">
        <h1 className="text-2xl font-bold text-[#1A5EF9]">Save Invoice</h1>

        {/* Products card */}
        <div className="rounded-[26px] border border-[#E8EEF9] bg-white shadow-[0_16px_40px_rgba(26,94,249,0.08)] p-6">
          <h2 className="text-lg font-semibold text-[#21314A] mb-5">
            Invoice Products
          </h2>
          {productArray.length === 0 ? (
            <div className="flex items-center justify-center py-12 text-[#52627C] text-sm">
              No products in this invoice.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <TableComponent
                columns={brandsTColumns}
                data={productArray.map((item, index) => ({
                  sl: (index + 1).toString(),
                  product:
                    findProductById(products, item.productId)?.productName ||
                    "",
                  inArabic:
                    findProductById(products, item.productId)?.productName ||
                    "",
                  quantity: item.quantity.toString(),
                  unit:
                    item.unit ||
                    findProductById(products, item.productId)?.unit ||
                    "",
                  tax: item.taxPercentage.toString() + "%",
                  unitPrice: item.unitPrice.toString(),
                  discount:
                    item.discountAmount.toString() +
                    (item.isDiscountPercentage ? "%" : ""),
                  total: (
                    (item.unitPrice * item.quantity -
                      (item.isDiscountPercentage
                        ? (item.unitPrice *
                            item.quantity *
                            item.discountAmount) /
                          100
                        : item.discountAmount)) *
                    (1 + item.taxPercentage / 100)
                  ).toFixed(3),
                  action: "",
                }))}
              />
            </div>
          )}
        </div>

        {/* Edit Product Modal */}
        <EditProductModal
          isOpen={isEditModalOpen}
          onClose={handleCloseModal}
          onSave={handleSaveEditedProduct}
          productData={editingProduct}
          products={products || []}
          editIndex={editingIndex}
        />

        {/* Summary + Payment card */}
        <div className="flex justify-end">
          <div className="rounded-[26px] border border-[#E8EEF9] bg-white shadow-[0_16px_40px_rgba(26,94,249,0.08)] p-6 w-full max-w-md">
            <h2 className="text-lg font-semibold text-[#21314A] mb-4">
              Summary &amp; Payment
            </h2>

            {/* Summary rows */}
            <div className="space-y-0 mb-5">
              <div className="flex justify-between items-center py-2.5 border-b border-dotted border-[#E8EEF9]">
                <span className="text-sm text-[#52627C]">Net Total</span>
                <span className="text-sm font-medium text-[#21314A]">
                  {productArray
                    .reduce(
                      (sum, item) => sum + item.unitPrice * item.quantity,
                      0,
                    )
                    .toFixed(3)}
                </span>
              </div>
              <div className="flex justify-between items-center py-2.5 border-b border-dotted border-[#E8EEF9]">
                <span className="text-sm text-[#52627C]">Tax</span>
                <span className="text-sm font-medium text-[#21314A]">
                  {productArray
                    .reduce((sum, item) => {
                      const base =
                        item.unitPrice * item.quantity -
                        (item.isDiscountPercentage
                          ? (item.unitPrice *
                              item.quantity *
                              item.discountAmount) /
                            100
                          : item.discountAmount);
                      return sum + (base * item.taxPercentage) / 100;
                    }, 0)
                    .toFixed(3)}
                </span>
              </div>
              <div className="flex justify-between items-center py-2.5 border-b border-dotted border-[#E8EEF9]">
                <span className="text-sm text-[#52627C]">Discount</span>
                <span className="text-sm font-medium text-[#21314A]">
                  {productArray
                    .reduce(
                      (sum, item) =>
                        item.isDiscountPercentage
                          ? sum +
                            (item.unitPrice *
                              item.quantity *
                              item.discountAmount) /
                              100
                          : sum + item.discountAmount,
                      0,
                    )
                    .toFixed(3)}
                </span>
              </div>
              <div className="flex justify-between items-center py-2.5 border-b border-dotted border-[#E8EEF9]">
                <span className="text-sm text-[#52627C]">Bill Discount</span>
                <span className="text-sm font-medium text-[#21314A]">0.00</span>
              </div>
              <div className="flex justify-between items-center py-2.5 border-b border-dotted border-[#E8EEF9]">
                <span className="text-sm text-[#52627C]">Rounding</span>
                <input
                  value={roundoff}
                  onChange={(e) => setRoundoff(Number(e.target.value))}
                  type="number"
                  className="h-9 w-28 rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-3 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
                />
              </div>
              <div className="flex justify-between items-center pt-3 pb-4 border-b border-[#E8EEF9]">
                <span className="text-base font-bold text-[#1A5EF9]">
                  Total
                </span>
                <span className="text-base font-bold text-[#1A5EF9]">
                  {grandTotal.toFixed(3)}
                </span>
              </div>
            </div>

            {/* Payment method pills */}
            <div className="mb-5">
              <p className="text-sm font-semibold text-[#21314A] mb-3">
                Payment Method
              </p>
              <div className="flex flex-wrap gap-2">
                {PAYMENT_MODES.map((mode) => (
                  <button
                    key={mode.value}
                    type="button"
                    onClick={() => {
                      setPayment((prev) => ({
                        ...prev,
                        paymentMethod: mode.value,
                      }));
                      setChqNumber("");
                      setCardNumber("");
                      setOnlinePaymentRefNo("");
                    }}
                    className={cn(
                      "rounded-[10px] px-5 py-2 text-sm font-semibold transition-colors",
                      payment.paymentMethod === mode.value
                        ? "bg-[#1A5EF9] text-white"
                        : "bg-[#F4F7FC] text-[#415043] hover:bg-[#E8EEF9]",
                    )}
                  >
                    {mode.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Conditional ref fields */}
            {payment.paymentMethod === "CHEQUE" && (
              <div className="mb-4">
                <label className="text-sm font-medium text-[#52627C] mb-1.5 block">
                  Cheque Number
                </label>
                <input
                  type="text"
                  value={chqNumber}
                  onChange={(e) => setChqNumber(e.target.value)}
                  placeholder="Enter cheque number"
                  className="h-11 w-full rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
                />
              </div>
            )}
            {payment.paymentMethod === "CARD" && (
              <div className="mb-4">
                <label className="text-sm font-medium text-[#52627C] mb-1.5 block">
                  Card Number
                </label>
                <input
                  type="text"
                  value={cardNumber}
                  onChange={(e) => setCardNumber(e.target.value)}
                  placeholder="Enter card number"
                  className="h-11 w-full rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
                />
              </div>
            )}
            {payment.paymentMethod === "ONLINE_PAYMENT" && (
              <div className="mb-4">
                <label className="text-sm font-medium text-[#52627C] mb-1.5 block">
                  Online Payment Ref No
                </label>
                <input
                  type="text"
                  value={onlinePaymentRefNo}
                  onChange={(e) => setOnlinePaymentRefNo(e.target.value)}
                  placeholder="Enter reference number"
                  className="h-11 w-full rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
                />
              </div>
            )}

            {/* Payment amount fields */}
            <div className="grid grid-cols-2 gap-3 mb-5">
              <div>
                <label className="text-sm font-medium text-[#52627C] mb-1.5 block">
                  Total Balance
                </label>
                <input
                  type="text"
                  value={grandTotal.toFixed(3)}
                  disabled
                  className="h-11 w-full rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none cursor-not-allowed opacity-60"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-[#52627C] mb-1.5 block">
                  Amount Received
                </label>
                <input
                  type="number"
                  name="amountPaid"
                  placeholder="Enter amount"
                  className="h-11 w-full rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
                  value={payment.amountPaid}
                  onChange={handleChange}
                />
              </div>
              <div className="col-span-2">
                <label className="text-sm font-medium text-[#52627C] mb-1.5 block">
                  Balance
                </label>
                <input
                  type="text"
                  value={(
                    grandTotal - (Number(payment.amountPaid) || 0)
                  ).toFixed(3)}
                  disabled
                  className="h-11 w-full rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none cursor-not-allowed opacity-60"
                />
              </div>
            </div>

            <div className="mb-5">
              <label className="text-sm font-medium text-[#52627C] mb-1.5 block">
                Req Number
              </label>
              <input
                type="text"
                value={reqNumber}
                onChange={(e) => setReqNumber(e.target.value)}
                placeholder="Enter req number"
                className={inputCls}
              />
            </div>

            <div className="mb-5">
              <label className="text-sm font-medium text-[#52627C] mb-1.5 block">
                LPO Number
              </label>
              <input
                type="text"
                value={lpoNumber}
                onChange={(e) => setLpoNumber(e.target.value)}
                placeholder="Enter LPO number"
                className={inputCls}
              />
            </div>

            <div className="mb-5">
              <label className="text-sm font-medium text-[#52627C] mb-1.5 block">
                Invoice Remark
              </label>
              <input
                type="text"
                value={invoiceRemark}
                onChange={(e) => setInvoiceRemark(e.target.value)}
                placeholder="Enter remark for this invoice"
                className="h-11 w-full rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
              />
            </div>

            <button
              onClick={onSaveQuotation}
              className="h-11 w-full rounded-[12px] bg-[#1A5EF9] text-white hover:bg-[#0E4FDE] font-medium text-sm transition-colors"
            >
              Save Invoice
            </button>
          </div>
        </div>
      </div>
      <QuoteSuccessModal
        isOpen={isSuccessModalOpen}
        onClose={handleCloseSuccessModal}
        onPrintQuote={handlePrintQuote}
        quoteId={quoteId ?? ""}
      />
    </>
  );
};

export default SaveInvoice;
